import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Save, ArrowLeft, Pause, Play, Copy, Target } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/agenda-antiprocrastinacao`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lumis_user_token')}` });

const STATUS_AGENDA_LABEL = { ativa: 'Ativa', pausada: 'Pausada', concluida: 'Concluída' };
const STATUS_AGENDA_COLOR = {
  ativa: 'bg-blue-100 text-blue-700',
  pausada: 'bg-gray-100 text-gray-600',
  concluida: 'bg-green-100 text-green-700',
};
const STATUS_DIA_LABEL = {
  aguardando_resposta: 'Aguardando resposta',
  sucesso: 'Sucesso',
  falha: 'Falha',
  sem_resposta: 'Sem resposta',
};
const STATUS_DIA_COLOR = {
  aguardando_resposta: 'bg-blue-100 text-blue-700',
  sucesso: 'bg-green-100 text-green-700',
  falha: 'bg-red-100 text-red-700',
  sem_resposta: 'bg-gray-100 text-gray-400',
};
const ANTECEDENCIA_LABEL = {
  no_momento: 'No momento do compromisso',
  '15min': '15 minutos antes',
  '30min': '30 minutos antes',
  '1h': '1 hora antes',
  '2h': '2 horas antes',
  '1dia': '1 dia antes',
};
const DIAS_SEMANA_LABEL = [
  { valor: 0, label: 'Segunda' },
  { valor: 1, label: 'Terça' },
  { valor: 2, label: 'Quarta' },
  { valor: 3, label: 'Quinta' },
  { valor: 4, label: 'Sexta' },
  { valor: 5, label: 'Sábado' },
  { valor: 6, label: 'Domingo' },
];

const fmtDate = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  titulo: '',
  recorrencia_modo: 'dias_semana',
  dias_semana: [0, 1, 2, 3, 4],
  intervalo_dias: 2,
  horario_compromisso: '07:30',
  lembrete_antecedencia: '15min',
  minutos_pos_checkin: 10,
  data_inicio: todayStr(),
  total_ocorrencias: 20,
});

export default function AgendaAntiprocrastinacaoSection() {
  const [view, setView] = useState('lista'); // lista | detalhe
  const [agendas, setAgendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [selecionada, setSelecionada] = useState(null);
  const [acting, setActing] = useState(false);

  const loadAgendas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agendas`, { headers: authHeaders() });
      setAgendas(res.data);
    } catch {
      toast.error('Erro ao carregar Agenda Antiprocrastinação');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAgendas(); }, [loadAgendas]);

  const abrirDetalhe = async (id) => {
    try {
      const res = await axios.get(`${API}/agendas/${id}`, { headers: authHeaders() });
      setSelecionada(res.data);
      setView('detalhe');
    } catch {
      toast.error('Erro ao carregar agenda');
    }
  };

  const toggleDiaSemana = (valor) => {
    setForm(prev => ({
      ...prev,
      dias_semana: prev.dias_semana.includes(valor)
        ? prev.dias_semana.filter(d => d !== valor)
        : [...prev.dias_semana, valor].sort(),
    }));
  };

  const handleCreate = async () => {
    if (form.titulo.trim().length < 5) { toast.error('Título deve ter ao menos 5 caracteres'); return; }
    if (form.recorrencia_modo === 'dias_semana' && form.dias_semana.length === 0) {
      toast.error('Selecione ao menos um dia da semana'); return;
    }
    if (form.recorrencia_modo === 'intervalo' && form.intervalo_dias < 1) {
      toast.error('Intervalo de dias inválido'); return;
    }
    if (form.total_ocorrencias < 1) { toast.error('Quantidade de dias do ciclo deve ser ao menos 1'); return; }

    const recorrencia = form.recorrencia_modo === 'dias_semana'
      ? { modo: 'dias_semana', dias_semana: form.dias_semana }
      : { modo: 'intervalo', intervalo_dias: form.intervalo_dias };

    setSaving(true);
    try {
      await axios.post(`${API}/agendas`, {
        titulo: form.titulo,
        recorrencia,
        horario_compromisso: form.horario_compromisso,
        lembrete_antecedencia: form.lembrete_antecedencia,
        minutos_pos_checkin: form.minutos_pos_checkin,
        data_inicio: form.data_inicio,
        total_ocorrencias: form.total_ocorrencias,
      }, { headers: authHeaders() });
      toast.success('Agenda Antiprocrastinação criada!');
      setShowForm(false);
      setForm(emptyForm());
      loadAgendas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar agenda');
    } finally {
      setSaving(false);
    }
  };

  const handlePausar = async () => {
    setActing(true);
    try {
      const res = await axios.put(`${API}/agendas/${selecionada.id}/pausar`, {}, { headers: authHeaders() });
      setSelecionada(res.data);
      toast.success('Agenda pausada');
      loadAgendas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao pausar');
    } finally {
      setActing(false);
    }
  };

  const handleRetomar = async () => {
    setActing(true);
    try {
      const res = await axios.put(`${API}/agendas/${selecionada.id}/retomar`, {}, { headers: authHeaders() });
      setSelecionada(res.data);
      toast.success('Agenda retomada');
      loadAgendas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao retomar');
    } finally {
      setActing(false);
    }
  };

  const handleDuplicar = async () => {
    setActing(true);
    try {
      await axios.post(`${API}/agendas/${selecionada.id}/duplicar`, {}, { headers: authHeaders() });
      toast.success('Novo ciclo criado a partir desta agenda!');
      setView('lista');
      loadAgendas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao duplicar');
    } finally {
      setActing(false);
    }
  };

  if (view === 'detalhe' && selecionada) {
    const hist = [...(selecionada.historico || [])].reverse();
    return (
      <div className="space-y-4" data-testid="antiproc-detalhe">
        <button
          onClick={() => setView('lista')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 font-manrope"
        >
          <ArrowLeft size={15} /> Voltar
        </button>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-outfit font-light text-gray-900">{selecionada.titulo}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_AGENDA_COLOR[selecionada.status]}`}>
              {STATUS_AGENDA_LABEL[selecionada.status]}
            </span>
          </div>
          <p className="text-sm text-gray-600 font-manrope">
            {selecionada.sucesso_count} de {selecionada.total_ocorrencias} dias com sucesso
          </p>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#1E3A8A] rounded-full"
              style={{ width: `${Math.min(100, (selecionada.sucesso_count / selecionada.total_ocorrencias) * 100)}%` }}
            />
          </div>

          <div className="flex gap-2 pt-2">
            {selecionada.status === 'ativa' && (
              <button
                data-testid="btn-pausar-antiproc"
                onClick={handlePausar}
                disabled={acting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full font-manrope disabled:opacity-50"
              >
                <Pause size={14} /> Pausar
              </button>
            )}
            {selecionada.status === 'pausada' && (
              <button
                data-testid="btn-retomar-antiproc"
                onClick={handleRetomar}
                disabled={acting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full font-manrope disabled:opacity-50"
              >
                <Play size={14} /> Retomar
              </button>
            )}
            {selecionada.status === 'concluida' && (
              <button
                data-testid="btn-duplicar-antiproc"
                onClick={handleDuplicar}
                disabled={acting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full font-manrope disabled:opacity-50"
              >
                <Copy size={14} /> Duplicar (novo ciclo)
              </button>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h4 className="text-sm font-manrope font-semibold text-gray-900 mb-3">Histórico</h4>
          {hist.length === 0 ? (
            <p className="text-sm text-gray-500 font-manrope">Ainda não há checkins registrados.</p>
          ) : (
            <div className="space-y-2">
              {hist.map((h) => (
                <div key={h.numero_ocorrencia} className="flex items-center justify-between text-sm font-manrope">
                  <span className="text-gray-600">Dia {h.numero_ocorrencia} · {fmtDate(h.data)}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${STATUS_DIA_COLOR[h.status]}`}>
                    {STATUS_DIA_LABEL[h.status]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="antiproc-lista">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-outfit font-light text-gray-900">Agenda Antiprocrastinação</h2>
          <p className="text-xs text-gray-500 font-manrope mt-0.5">Compromisso diário com checkin de responsabilização por WhatsApp</p>
        </div>
        <button
          data-testid="btn-nova-antiproc"
          onClick={() => { setForm(emptyForm()); setShowForm(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors"
        >
          <Plus size={15} /> Nova Agenda
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : agendas.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Target size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-manrope">Nenhuma Agenda Antiprocrastinação criada ainda</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {agendas.map(a => (
            <button
              key={a.id}
              data-testid={`antiproc-card-${a.id}`}
              onClick={() => abrirDetalhe(a.id)}
              className="text-left bg-white border border-gray-200 rounded-2xl p-4 hover:border-[#1E3A8A]/40 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900 font-manrope">{a.titulo}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${STATUS_AGENDA_COLOR[a.status]}`}>
                  {STATUS_AGENDA_LABEL[a.status]}
                </span>
              </div>
              <p className="text-xs text-gray-500 font-manrope">
                {a.sucesso_count} de {a.total_ocorrencias} dias com sucesso · compromisso às {a.horario_compromisso}
              </p>
            </button>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-gray-300 rounded-2xl p-6 w-full max-w-lg shadow-2xl my-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-outfit font-light text-gray-900">Nova Agenda Antiprocrastinação</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-900">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 font-manrope">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Título *</label>
                <input
                  data-testid="input-antiproc-titulo"
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black placeholder-gray-400 text-sm focus:outline-none focus:border-[#1E3A8A]"
                  value={form.titulo}
                  onChange={e => setForm({ ...form, titulo: e.target.value })}
                  placeholder="Ex: Chegar sempre no horário"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Quando esse compromisso se repete?</label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                    <input
                      type="radio"
                      name="recorrencia-modo"
                      checked={form.recorrencia_modo === 'dias_semana'}
                      onChange={() => setForm({ ...form, recorrencia_modo: 'dias_semana' })}
                      className="w-4 h-4 accent-[#1E3A8A]"
                    />
                    Dias da semana
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                    <input
                      type="radio"
                      name="recorrencia-modo"
                      checked={form.recorrencia_modo === 'intervalo'}
                      onChange={() => setForm({ ...form, recorrencia_modo: 'intervalo' })}
                      className="w-4 h-4 accent-[#1E3A8A]"
                    />
                    A cada N dias
                  </label>
                </div>
                {form.recorrencia_modo === 'dias_semana' ? (
                  <div className="grid grid-cols-2 gap-2">
                    {DIAS_SEMANA_LABEL.map(d => (
                      <label key={d.valor} className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                        <input
                          data-testid={`checkbox-dia-${d.valor}`}
                          type="checkbox"
                          checked={form.dias_semana.includes(d.valor)}
                          onChange={() => toggleDiaSemana(d.valor)}
                          className="w-4 h-4 accent-[#1E3A8A]"
                        />
                        {d.label}
                      </label>
                    ))}
                  </div>
                ) : (
                  <input
                    data-testid="input-intervalo-dias"
                    type="number"
                    min="1"
                    className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                    value={form.intervalo_dias}
                    onChange={e => setForm({ ...form, intervalo_dias: Math.max(1, parseInt(e.target.value) || 1) })}
                    placeholder="Ex: 2 (dias alternados)"
                  />
                )}
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Horário do compromisso *</label>
                <input
                  data-testid="input-antiproc-horario"
                  type="time"
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                  value={form.horario_compromisso}
                  onChange={e => setForm({ ...form, horario_compromisso: e.target.value })}
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Antecedência do lembrete</label>
                <div className="flex flex-col gap-2">
                  {Object.entries(ANTECEDENCIA_LABEL).map(([k, v]) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                      <input
                        type="radio"
                        name="antiproc-antecedencia"
                        checked={form.lembrete_antecedencia === k}
                        onChange={() => setForm({ ...form, lembrete_antecedencia: k })}
                        className="w-4 h-4 accent-[#1E3A8A]"
                      />
                      {v}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p data-testid="texto-antiproc-checkin" className="text-sm text-gray-500 font-manrope">
                  Após 10 min. do seu compromisso, vou checar com você se deu tudo certo.
                </p>
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Data de início *</label>
                <input
                  data-testid="input-antiproc-data-inicio"
                  type="date"
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                  value={form.data_inicio}
                  onChange={e => setForm({ ...form, data_inicio: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Quantidade de dias do ciclo *</label>
                <input
                  data-testid="input-antiproc-total-ocorrencias"
                  type="number"
                  min="1"
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                  value={form.total_ocorrencias}
                  onChange={e => setForm({ ...form, total_ocorrencias: Math.max(1, parseInt(e.target.value) || 1) })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 font-manrope">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 border border-gray-300 rounded-full transition-colors">
                Cancelar
              </button>
              <button
                data-testid="btn-salvar-antiproc"
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full font-medium text-sm transition-colors disabled:opacity-50"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={15} />}
                Criar Agenda
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
